import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import AddIcons from '@/icons/AddIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    batchCreateBalanceSheetItems,
    listBalanceSheetItems,
} from '@/modules/Report/components/BusinessPerformance/services'
import {
    BalanceSheetSummary,
    BatchBalanceSheetCreateParams,
} from '@/modules/Report/components/BusinessPerformance/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, IconButton, Typogaphy } from 'vnetwork-ui'
import * as yup from 'yup'

export interface RatioFormModalProps extends ModalProps {
    refetch?: () => void
    editData?: BalanceSheetSummary | null
    mode: 'create' | 'edit'
}

interface ItemForm {
    item_name: string
    amount: number
    currency: string
    notes?: string
    description?: string
}

interface FormData {
    report_month: number
    report_year: number
    assets: ItemForm[]
    liabilities: ItemForm[]
    equities: ItemForm[]
}

const currentYear = dayjs().year()

const RatioFormModal: React.FC<RatioFormModalProps> = ({
    open,
    onClose,
    refetch,
    editData,
    mode,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                assets: yup.array().of(
                    yup.object().shape({
                        item_name: yup.string().required(t('required')),
                        amount: yup
                            .number()
                            .min(0, t('must_be_positive'))
                            .required(t('required')),
                        currency: yup.string().required(t('required')),
                    })
                ),
                liabilities: yup.array().of(
                    yup.object().shape({
                        item_name: yup.string().required(t('required')),
                        amount: yup
                            .number()
                            .min(0, t('must_be_positive'))
                            .required(t('required')),
                        currency: yup.string().required(t('required')),
                    })
                ),
                equities: yup.array().of(
                    yup.object().shape({
                        item_name: yup.string().required(t('required')),
                        amount: yup
                            .number()
                            .min(0, t('must_be_positive'))
                            .required(t('required')),
                        currency: yup.string().required(t('required')),
                    })
                ),
            }),
        [t]
    )

    // Default values
    const defaultValues: FormData = useMemo(
        () => ({
            report_month: dayjs().month() + 1,
            report_year: dayjs().year(),
            assets: [{ item_name: '', amount: 0, currency: 'VND' }],
            liabilities: [{ item_name: '', amount: 0, currency: 'VND' }],
            equities: [{ item_name: '', amount: 0, currency: 'VND' }],
        }),
        []
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    const {
        fields: assetFields,
        append: appendAsset,
        remove: removeAsset,
    } = useFieldArray({ control, name: 'assets' })

    const {
        fields: liabilityFields,
        append: appendLiability,
        remove: removeLiability,
    } = useFieldArray({ control, name: 'liabilities' })

    const {
        fields: equityFields,
        append: appendEquity,
        remove: removeEquity,
    } = useFieldArray({ control, name: 'equities' })

    // Load existing items when in edit mode
    useEffect(() => {
        const loadExistingData = async () => {
            if (open && mode === 'edit' && editData) {
                setIsLoading(true)
                try {
                    // Fetch detailed items for this month
                    const items = await listBalanceSheetItems({
                        report_month: editData.report_month,
                    })

                    const assets = items.filter((i) => i.item_type === 'asset')
                    const liabilities = items.filter(
                        (i) => i.item_type === 'liability'
                    )
                    const equities = items.filter(
                        (i) => i.item_type === 'equity'
                    )

                    const [year, month] = editData.report_month.split('-') || [
                        dayjs().year(),
                        dayjs().month() + 1,
                    ]
                    reset({
                        report_month: parseInt(month),
                        report_year: parseInt(year),
                        assets:
                            assets.length > 0
                                ? assets.map((a) => ({
                                      item_name: a.item_name,
                                      amount: a.amount,
                                      currency: a.currency,
                                      notes: a.notes,
                                      description: a.description,
                                  }))
                                : [
                                      {
                                          item_name: '',
                                          amount: 0,
                                          currency: 'VND',
                                      },
                                  ],
                        liabilities:
                            liabilities.length > 0
                                ? liabilities.map((l) => ({
                                      item_name: l.item_name,
                                      amount: l.amount,
                                      currency: l.currency,
                                      notes: l.notes,
                                      description: l.description,
                                  }))
                                : [
                                      {
                                          item_name: '',
                                          amount: 0,
                                          currency: 'VND',
                                      },
                                  ],
                        equities:
                            equities.length > 0
                                ? equities?.map((e) => ({
                                      item_name: e.item_name,
                                      amount: e.amount,
                                      currency: e.currency,
                                      notes: e.notes,
                                      description: e.description,
                                  }))
                                : [
                                      {
                                          item_name: '',
                                          amount: 0,
                                          currency: 'VND',
                                      },
                                  ],
                    })
                } catch (error) {
                    console.error('Failed to load existing items:', error)
                    toast.error(t('failed_to_load_data'))
                } finally {
                    setIsLoading(false)
                }
            } else if (open && mode === 'create') {
                reset(defaultValues)
            }
        }

        loadExistingData()
    }, [open, editData, mode, reset, defaultValues, t])

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)

            const payload: BatchBalanceSheetCreateParams = {
                report_month: `${values.report_year}-${values.report_month}`,
                assets: values.assets
                    .filter((a) => a.item_name && a.amount > 0)
                    .map((a) => ({
                        item_name: a.item_name,
                        amount: a.amount,
                        currency: a.currency || 'VND',
                        notes: a.notes,
                        description: a.description,
                    })),
                liabilities: values.liabilities
                    .filter((l) => l.item_name && l.amount > 0)
                    .map((l) => ({
                        item_name: l.item_name,
                        amount: l.amount,
                        currency: l.currency || 'VND',
                        notes: l.notes,
                        description: l.description,
                    })),
                equities: values.equities
                    .filter((e) => e.item_name && e.amount > 0)
                    .map((e) => ({
                        item_name: e.item_name,
                        amount: e.amount,
                        currency: e.currency || 'VND',
                        notes: e.notes,
                        description: e.description,
                    })),
            }

            // Check if at least one item exists
            if (
                payload.assets!.length === 0 &&
                payload.liabilities!.length === 0 &&
                payload.equities!.length === 0
            ) {
                toast.error(t('at_least_one_item_required'))
                setIsSubmitting(false)
                return
            }

            // Upsert (create or update based on report_month)
            await batchCreateBalanceSheetItems(payload)

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_BALANCE_SHEET_MONTHLY],
            })

            // Show success message
            toast.success(
                mode === 'edit' ? t('update-success') : t('create-success')
            )

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to save balance sheet:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const renderItemSection = (
        title: string,
        fields: any[],
        append: (item: ItemForm) => void,
        remove: (index: number) => void,
        fieldName: 'assets' | 'liabilities' | 'equities'
    ) => (
        <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between">
                <Typogaphy className="text-sm font-semibold text-gray-700">
                    {title}
                </Typogaphy>
                <Button
                    type="button"
                    size="small"
                    className="min-w-[200px]"
                    variant="outlined"
                    startIcon={<AddIcons />}
                    onClick={() =>
                        append({ item_name: '', amount: 0, currency: 'VND' })
                    }
                >
                    {t('add')}
                </Button>
            </div>

            {fields.map((field, index) => (
                <div className="grid grid-cols-12 gap-4" key={field.id}>
                    <div className="col-span-1 flex items-center justify-center">
                        <IconButton
                            size="small"
                            className="border"
                            onClick={() => remove(index)}
                        >
                            <Typogaphy>{index + 1}</Typogaphy>
                        </IconButton>
                    </div>
                    <FormItem
                        control={control}
                        name={`${fieldName}.${index}.item_name`}
                        type="textfield"
                        label={t('item_name')}
                        placeholder={t('enter_item_name')}
                        className="col-span-6"
                    />
                    <div className="col-span-5 flex items-center gap-2">
                        <FormItem
                            control={control}
                            name={`${fieldName}.${index}.amount`}
                            type="number"
                            label={t('amount')}
                            placeholder="0"
                            className="flex-1"
                        />
                        <IconButton
                            size="small"
                            className="border hover:border-red-500 hover:text-red-500"
                            onClick={() => remove(index)}
                        >
                            <DeleteOutlineIcons />
                        </IconButton>
                    </div>
                </div>
            ))}
        </div>
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                mode === 'edit'
                    ? t('edit_balance_sheet')
                    : t('create_balance_sheet')
            }
            maxWidth="lg"
        >
            {isLoading ? (
                <div className="flex items-center justify-center p-8">
                    <div className="text-gray-500">{t('loading')}...</div>
                </div>
            ) : (
                <form
                    onSubmit={handleSubmit(onSubmit as any)}
                    className="space-y-4 p-4 max-h-[80vh] overflow-y-auto"
                >
                    {/* Report Month */}
                    <FormItem
                        control={control}
                        name="report_year"
                        type="select"
                        label={t('year')}
                        disabled={mode === 'edit'}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('year')} ${currentYear - index}`,
                            value: currentYear - index,
                        }))}
                        allowClear={false}
                    />
                    <FormItem
                        control={control}
                        name="report_month"
                        type="select"
                        label={t('month')}
                        disabled={mode === 'edit'}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('month')} ${index + 1}`,
                            value: index + 1,
                        }))}
                        allowClear={false}
                    />

                    {/* Assets */}
                    {renderItemSection(
                        t('assets'),
                        assetFields,
                        appendAsset,
                        removeAsset,
                        'assets'
                    )}

                    {/* Liabilities */}
                    {renderItemSection(
                        t('liabilities'),
                        liabilityFields,
                        appendLiability,
                        removeLiability,
                        'liabilities'
                    )}

                    {/* Equities */}
                    {renderItemSection(
                        t('equity'),
                        equityFields,
                        appendEquity,
                        removeEquity,
                        'equities'
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-2 sticky bottom-0 bg-white">
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            {mode === 'edit' ? t('update') : t('create')}
                        </Button>
                    </div>
                </form>
            )}
        </Dialog>
    )
}

export default RatioFormModal
